import type { RpeCopy, SupportedMarketingLocale } from "./types";

export const rpe: Record<SupportedMarketingLocale, RpeCopy> = {
  en: {
    seo: {
      title: "Exertion tracking for grassroots coaches — Vote.GameOn",
      description:
        "Pro-grade RPE load monitoring for grassroots football. One-tap player responses, ACWR injury-risk tracking, no player app required.",
    },
    srHeading: "Exertion tracking that works — one tap from every player",
    hero: {
      eyebrow: "Exertion tracking, made simple",
      headline: "Know how hard your players are working.",
      headlineHighlight: "Before they break.",
      subhead:
        "One tap on a number from 1 to 10 after every session. The same method elite clubs use — adapted for grassroots coaches and the parents who run them.",
      cta: "Start a free team",
      seeHow: "See how it works",
      subLinks: {
        vote: "See Player's Player Voting →",
        rsvp: "See how RSVP works →",
        rpe: "See how Exertion tracking works →",
        feedback: "See how Coach Feedback works →",
      },
      mockSession: "Tuesday training",
      mockQuestion: "How hard was it?",
      mockFooter: "One tap. No login. Coach sees it instantly.",
      mockBadge: "Workload healthy · ACWR 0.9",
    },
    problem: {
      eyebrow: "The hidden problem in grassroots training",
      cards: [
        { title: "You can't see fatigue", body: "By the time a player limps off, the warning signs were two weeks ago." },
        { title: "Every player is different", body: "Same drills, very different loads. Without numbers it's all guesswork." },
        { title: "Spikes break players", body: "Sudden jumps in workload are the strongest predictor of soft-tissue injury." },
      ],
    },
    howItWorks: {
      heading: "Three steps. One tap from each player.",
      subhead: "From the final whistle to a complete picture of training load — under five minutes.",
      steps: [
        { badge: "5 seconds", title: "Coach sends after training", body: "One tap fires SMS or email to every player. No app to install, no logins to chase." },
        { badge: "1 tap", title: "Player taps a number", body: "1 to 10, with plain-language labels. Most replies land within minutes." },
        { badge: "Live", title: "Coach sees the load", body: "Per-player trends, ACWR injury-risk band, and a team-by-session grid in real time." },
      ],
    },
    scale: {
      heading: "The whole scale on one screen",
      subhead:
        "A 1–10 scale with plain-language labels. The same scale used by sports scientists — translated for kids, parents, and weekend coaches.",
      footnote:
        "Players see the same colours and labels in the app — so a \"7\" means the same thing on Tuesday as it does in May.",
      items: [
        { label: "Rest", description: "Hardly any exertion" },
        { label: "Very Light", description: "Easy, can talk freely" },
        { label: "Light", description: "Recovery pace, comfortable" },
        { label: "Moderate", description: "Breathing heavier, can converse" },
        { label: "Medium", description: "Normal training effort" },
        { label: "Hard", description: "Short of breath, can speak a sentence" },
        { label: "Very Hard", description: "Borderline uncomfortable" },
        { label: "Extremely Hard", description: "Very difficult to maintain" },
        { label: "Near Max", description: "Almost impossible to keep going" },
        { label: "Maximum", description: "All-out, cannot maintain" },
      ],
    },
    features: {
      heading: "Built for the coach who's also the parent, the manager, and the kit washer",
      subhead: "Pro-grade load monitoring without the pro-grade overhead.",
      cards: [
        { title: "No player accounts", body: "Players respond from a one-time link in their SMS or email. Nothing to install, nothing to remember." },
        { title: "SMS and email delivery", body: "Reach every household where they already are. Choose per session, or default to whatever's on file." },
        { title: "Injury-risk band (ACWR)", body: "A traffic-light view of the acute-to-chronic workload ratio — the number elite clubs use to spot injury risk before it happens.", hrefLabel: "What's ACWR?" },
        { title: "Team-by-session grid", body: "One scrollable view of every player across every session. Spot the player who's quietly cooked." },
        { title: "Target RPE on plans", body: "Set a target before training; flag any actual response that drifts more than two points away." },
        { title: "Pay only for delivery", body: "Coaches use the app for free. Top up small SMS/email credit packs as needed — no subscription." },
      ],
    },
    acwr: {
      eyebrow: "New to the term?",
      heading: "Wait — what's ACWR (Acute-to-Chronic Workload Ratio)?",
      subhead: "The one number that quietly predicts soft-tissue injuries — broken into plain English.",
      pieces: [
        { title: "Acute load", sub: "Last 7 days", body: "How hard the player has been working this week — every session, added up." },
        { title: "Chronic load", sub: "Rolling 28-day average", body: "What their body is used to over the last month — the baseline they've adapted to." },
        { title: "The ratio", sub: "Acute ÷ Chronic", body: "When this week jumps well above the recent average, the body hasn't adapted yet — and that's where injuries spike." },
      ],
      bandTitle: "The four zones of training load",
      bandSubtitle: "Same colours you'll see in the coach dashboard",
      zones: [
        { label: "Under-load", range: "< 0.8" },
        { label: "Sweet spot", range: "0.8 – 1.3" },
        { label: "Caution", range: "1.3 – 1.5" },
        { label: "Spike risk", range: "> 1.5" },
      ],
      targetBadge: "Target",
      closingBody:
        "Pro clubs have tracked this for years using GPS vests and sport scientists. We compute it from a single tap from each player after training.",
      closingLinkLabel: "See the research",
    },
    science: {
      eyebrow: "Backed by sports science",
      heading: "Session-RPE is the most-validated load monitoring method on the planet.",
      body: "Pioneered by Foster (1998) and refined into the Acute:Chronic Workload Ratio (Gabbett 2016), session-RPE is used by Premier League clubs, Olympic federations, and every elite training environment we know of. We just made it tappable from a kitchen table.",
      link: "Read the research behind it",
    },
    comparison: {
      heading: "Why not just use a wearable or a spreadsheet?",
      subhead: "Wearables miss what the player feels. Spreadsheets die in the boot of the car.",
      columnFeature: "Capability",
      columnUs: "Vote.GameOn",
      columnThem: "Wearables / sheets",
      rows: [
        "Works without a player app",
        "Captures perceived effort, not just heart rate",
        "Free for coaches to use",
        "Built-in injury-risk view (ACWR)",
        "Set target RPE per session",
        "Designed for grassroots, not pro academies",
      ],
      footnote: "Comparison reflects general positioning of consumer wearables and DIY tracking as of 2025.",
    },
    finalCta: {
      heading: "Start tracking exertion ",
      headlineHighlight: "this week.",
      subhead: "Free for coaches. No card needed. First handful of session deliveries on us.",
      cta: "Create your team — free",
      footnote: "One tap for the player. One number for the coach.",
    },
  },
  es: {
    seo: {
      title: "Monitoreo de carga (RPE) para entrenadores amateurs — Vote.GameOn",
      description:
        "Monitoreo de carga RPE de nivel profesional para fútbol amateur. Respuesta del jugador con un toque, riesgo de lesión por ACWR, sin app para los jugadores.",
    },
    srHeading: "Monitoreo de carga que sí funciona — un toque por jugador",
    hero: {
      eyebrow: "Monitoreo de carga, simple",
      headline: "Sabe qué tan duro están trabajando tus jugadores.",
      headlineHighlight: "Antes de que se rompan.",
      subhead:
        "Un toque en un número del 1 al 10 después de cada sesión. El mismo método que usan los clubes de élite — adaptado para entrenadores amateurs y los padres que los llevan.",
      cta: "Crear un equipo gratis",
      seeHow: "Cómo funciona",
      subLinks: {
        vote: "Ver la votación del jugador →",
        rsvp: "Ver cómo funciona RSVP →",
        rpe: "Ver el monitoreo de carga (RPE) →",
        feedback: "Ver la evaluación del entrenador →",
      },
      mockSession: "Entrenamiento del martes",
      mockQuestion: "¿Qué tan duro fue?",
      mockFooter: "Un toque. Sin login. El entrenador lo ve al instante.",
      mockBadge: "Carga sana · ACWR 0,9",
    },
    problem: {
      eyebrow: "El problema oculto del entrenamiento amateur",
      cards: [
        { title: "No puedes ver la fatiga", body: "Cuando un jugador sale cojeando, las señales ya estaban hace dos semanas." },
        { title: "Cada jugador es distinto", body: "Mismos ejercicios, cargas muy diferentes. Sin números, todo es adivinanza." },
        { title: "Los picos rompen jugadores", body: "Los saltos repentinos de carga son el predictor más fuerte de lesiones de tejido blando." },
      ],
    },
    howItWorks: {
      heading: "Tres pasos. Un toque por jugador.",
      subhead: "Del pitazo final a una imagen completa de la carga — en menos de cinco minutos.",
      steps: [
        { badge: "5 segundos", title: "El entrenador lo envía después del entrenamiento", body: "Con un toque salen SMS o emails a todos los jugadores. Sin app que instalar, sin logins que perseguir." },
        { badge: "1 toque", title: "El jugador toca un número", body: "Del 1 al 10, con etiquetas en lenguaje claro. La mayoría responde en minutos." },
        { badge: "En vivo", title: "El entrenador ve la carga", body: "Tendencia por jugador, semáforo de ACWR y una grilla equipo-por-sesión, todo en tiempo real." },
      ],
    },
    scale: {
      heading: "Toda la escala en una sola pantalla",
      subhead:
        "Una escala del 1 al 10 con etiquetas claras. La misma que usan los científicos del deporte — traducida para chicos, padres y entrenadores de fin de semana.",
      footnote:
        "Los jugadores ven los mismos colores y etiquetas en la app — así un \"7\" significa lo mismo el martes que en mayo.",
      items: [
        { label: "Reposo", description: "Casi sin esfuerzo" },
        { label: "Muy suave", description: "Fácil, puedes hablar normal" },
        { label: "Suave", description: "Ritmo de recuperación, cómodo" },
        { label: "Moderado", description: "Respiras más fuerte, todavía puedes conversar" },
        { label: "Medio", description: "Esfuerzo normal de entrenamiento" },
        { label: "Duro", description: "Sin aire, alcanzas a decir una frase" },
        { label: "Muy duro", description: "Al límite de lo incómodo" },
        { label: "Extremadamente duro", description: "Muy difícil de sostener" },
        { label: "Casi al máximo", description: "Casi imposible seguir" },
        { label: "Máximo", description: "Todo o nada, no se puede sostener" },
      ],
    },
    features: {
      heading: "Hecho para el entrenador que también es padre, manager y lavador de uniformes",
      subhead: "Monitoreo de carga de nivel profesional sin el costo profesional.",
      cards: [
        { title: "Sin cuentas para los jugadores", body: "Los jugadores responden desde un enlace único por SMS o email. Nada que instalar, nada que recordar." },
        { title: "Envío por SMS y email", body: "Llegas a cada hogar donde ya están. Eliges por sesión, o usas el método guardado por defecto." },
        { title: "Semáforo de riesgo (ACWR)", body: "Vista en semáforo del ratio carga aguda/crónica — el número que usan los clubes de élite para detectar el riesgo de lesión antes de que ocurra.", hrefLabel: "¿Qué es ACWR?" },
        { title: "Grilla equipo-por-sesión", body: "Una sola vista con todos los jugadores en todas las sesiones. Detectas al jugador que está fundido sin hacer ruido." },
        { title: "RPE objetivo en la planificación", body: "Define un objetivo antes del entrenamiento; marca cualquier respuesta real que se desvíe más de dos puntos." },
        { title: "Pagas solo por el envío", body: "Los entrenadores usan la app gratis. Recargas paquetes pequeños de créditos para SMS/email cuando los necesitas — sin suscripción." },
      ],
    },
    acwr: {
      eyebrow: "¿Nuevo con este término?",
      heading: "Espera — ¿qué es ACWR (ratio carga aguda/crónica)?",
      subhead: "El número que predice silenciosamente las lesiones de tejido blando — explicado en lenguaje claro.",
      pieces: [
        { title: "Carga aguda", sub: "Últimos 7 días", body: "Cuánto ha trabajado el jugador esta semana — cada sesión sumada." },
        { title: "Carga crónica", sub: "Promedio móvil de 28 días", body: "A lo que está acostumbrado su cuerpo el último mes — la base a la que ya se adaptó." },
        { title: "El ratio", sub: "Aguda ÷ Crónica", body: "Cuando esta semana salta muy por encima del promedio reciente, el cuerpo aún no se adapta — y ahí es donde se disparan las lesiones." },
      ],
      bandTitle: "Las cuatro zonas de carga de entrenamiento",
      bandSubtitle: "Los mismos colores que verás en el panel del entrenador",
      zones: [
        { label: "Carga baja", range: "< 0,8" },
        { label: "Zona ideal", range: "0,8 – 1,3" },
        { label: "Precaución", range: "1,3 – 1,5" },
        { label: "Riesgo de pico", range: "> 1,5" },
      ],
      targetBadge: "Objetivo",
      closingBody:
        "Los clubes profesionales llevan años midiendo esto con chalecos GPS y científicos del deporte. Nosotros lo calculamos a partir de un solo toque por jugador después del entrenamiento.",
      closingLinkLabel: "Ver la investigación",
    },
    science: {
      eyebrow: "Respaldado por la ciencia del deporte",
      heading: "El sRPE es el método de monitoreo de carga más validado del planeta.",
      body: "Iniciado por Foster (1998) y refinado en el ratio carga aguda/crónica (Gabbett 2016), el sRPE lo usan clubes de Premier League, federaciones olímpicas y todo entorno de entrenamiento de élite que conocemos. Nosotros solo lo hicimos tan fácil que se contesta desde la mesa de la cocina.",
      link: "Lee la investigación que lo respalda",
    },
    comparison: {
      heading: "¿Por qué no usar un wearable o una hoja de cálculo?",
      subhead: "Los wearables se pierden lo que el jugador siente. Las hojas de cálculo se mueren en el baúl del auto.",
      columnFeature: "Capacidad",
      columnUs: "Vote.GameOn",
      columnThem: "Wearables / hojas",
      rows: [
        "Funciona sin app para el jugador",
        "Captura esfuerzo percibido, no solo frecuencia cardiaca",
        "Gratis para los entrenadores",
        "Vista de riesgo de lesión integrada (ACWR)",
        "Define un RPE objetivo por sesión",
        "Pensado para el fútbol amateur, no academias profesionales",
      ],
      footnote: "La comparación refleja el posicionamiento general de los wearables de consumo y el seguimiento manual a 2025.",
    },
    finalCta: {
      heading: "Empieza a medir la carga ",
      headlineHighlight: "esta semana.",
      subhead: "Gratis para entrenadores. Sin tarjeta. Los primeros envíos de sesión van por nuestra cuenta.",
      cta: "Crea tu equipo — gratis",
      footnote: "Un toque para el jugador. Un número para el entrenador.",
    },
  },
  "pt-BR": {
    seo: {
      title: "Monitoramento de carga (PSE) para treinadores de base — Vote.GameOn",
      description:
        "Monitoramento de carga PSE de nível profissional para o futebol de base. Resposta do jogador com um toque, risco de lesão por ACWR, sem app para os jogadores.",
    },
    srHeading: "Monitoramento de carga que funciona — um toque por jogador",
    hero: {
      eyebrow: "Monitoramento de carga, simples",
      headline: "Saiba o quanto seus jogadores estão se exigindo.",
      headlineHighlight: "Antes que se machuquem.",
      subhead:
        "Um toque num número de 1 a 10 depois de cada sessão. O mesmo método dos clubes de elite — adaptado para os treinadores de base e para os pais que assumem essa função.",
      cta: "Criar um time grátis",
      seeHow: "Como funciona",
      subLinks: {
        vote: "Ver a votação dos jogadores →",
        rsvp: "Ver como funciona a presença →",
        rpe: "Ver o monitoramento de carga (PSE) →",
        feedback: "Ver a avaliação do treinador →",
      },
      mockSession: "Treino de terça",
      mockQuestion: "Quão duro foi?",
      mockFooter: "Um toque. Sem login. O treinador vê na hora.",
      mockBadge: "Carga saudável · ACWR 0,9",
    },
    problem: {
      eyebrow: "O problema oculto do treino de base",
      cards: [
        { title: "Você não enxerga a fadiga", body: "Quando o jogador sai mancando, os sinais já estavam ali há duas semanas." },
        { title: "Cada jogador é diferente", body: "Mesmos exercícios, cargas muito diferentes. Sem números, é tudo achismo." },
        { title: "Picos quebram jogadores", body: "Saltos repentinos de carga são o melhor preditor de lesões musculares." },
      ],
    },
    howItWorks: {
      heading: "Três passos. Um toque por jogador.",
      subhead: "Do apito final a uma visão completa da carga — em menos de cinco minutos.",
      steps: [
        { badge: "5 segundos", title: "O treinador envia após o treino", body: "Um toque dispara SMS ou e-mail para todos os jogadores. Nada para instalar, nenhum login para cobrar." },
        { badge: "1 toque", title: "O jogador toca um número", body: "De 1 a 10, com rótulos em linguagem clara. A maioria responde em minutos." },
        { badge: "Ao vivo", title: "O treinador vê a carga", body: "Tendência por jogador, semáforo de ACWR e uma grade equipe-por-sessão, tudo em tempo real." },
      ],
    },
    scale: {
      heading: "A escala inteira numa tela só",
      subhead:
        "Uma escala de 1 a 10 com rótulos claros. A mesma usada pelos cientistas do esporte — traduzida para crianças, pais e treinadores de fim de semana.",
      footnote:
        "Os jogadores veem as mesmas cores e rótulos no app — assim um \"7\" significa o mesmo na terça e em maio.",
      items: [
        { label: "Repouso", description: "Quase nenhum esforço" },
        { label: "Muito leve", description: "Fácil, dá para conversar normalmente" },
        { label: "Leve", description: "Ritmo de recuperação, confortável" },
        { label: "Moderado", description: "Respiração mais forte, ainda dá para conversar" },
        { label: "Médio", description: "Esforço normal de treino" },
        { label: "Difícil", description: "Sem ar, dá só para uma frase" },
        { label: "Muito difícil", description: "No limite do desconfortável" },
        { label: "Extremamente difícil", description: "Muito difícil de manter" },
        { label: "Quase no máximo", description: "Quase impossível continuar" },
        { label: "Máximo", description: "Tudo ou nada, não dá para sustentar" },
      ],
    },
    features: {
      heading: "Feito para o treinador que também é pai, gerente e quem lava o uniforme",
      subhead: "Monitoramento de carga de nível profissional sem o custo profissional.",
      cards: [
        { title: "Sem contas para os jogadores", body: "Os jogadores respondem por um link único no SMS ou e-mail. Nada para instalar, nada para lembrar." },
        { title: "Envio por SMS e e-mail", body: "Chega em cada casa onde já estão. Escolha por sessão, ou use o método padrão." },
        { title: "Semáforo de risco (ACWR)", body: "Vista em semáforo da razão carga aguda/crônica — o número que clubes de elite usam para detectar risco de lesão antes que aconteça.", hrefLabel: "O que é ACWR?" },
        { title: "Grade equipe-por-sessão", body: "Uma única vista com todos os jogadores em todas as sessões. Detecte quem está silenciosamente esgotado." },
        { title: "PSE alvo no planejamento", body: "Defina um alvo antes do treino; sinalize qualquer resposta real que se desvie em mais de dois pontos." },
        { title: "Pague só pelo envio", body: "Os treinadores usam o app de graça. Recarregue pequenos pacotes de créditos para SMS/e-mail conforme precisar — sem assinatura." },
      ],
    },
    acwr: {
      eyebrow: "Novo no termo?",
      heading: "Espera — o que é ACWR (razão carga aguda/crônica)?",
      subhead: "O número que prevê silenciosamente as lesões musculares — explicado em linguagem clara.",
      pieces: [
        { title: "Carga aguda", sub: "Últimos 7 dias", body: "O quanto o jogador trabalhou nesta semana — cada sessão, somada." },
        { title: "Carga crônica", sub: "Média móvel de 28 dias", body: "Aquilo a que o corpo se acostumou no último mês — a base à qual já se adaptou." },
        { title: "A razão", sub: "Aguda ÷ Crônica", body: "Quando esta semana salta bem acima da média recente, o corpo ainda não se adaptou — e é aí que as lesões disparam." },
      ],
      bandTitle: "As quatro zonas de carga de treino",
      bandSubtitle: "As mesmas cores que você verá no painel do treinador",
      zones: [
        { label: "Subcarga", range: "< 0,8" },
        { label: "Zona ideal", range: "0,8 – 1,3" },
        { label: "Cuidado", range: "1,3 – 1,5" },
        { label: "Risco de pico", range: "> 1,5" },
      ],
      targetBadge: "Alvo",
      closingBody:
        "Clubes profissionais acompanham isso há anos com coletes GPS e cientistas do esporte. Nós calculamos a partir de um único toque por jogador após o treino.",
      closingLinkLabel: "Ver a pesquisa",
    },
    science: {
      eyebrow: "Respaldado pela ciência do esporte",
      heading: "O sRPE é o método de monitoramento de carga mais validado do planeta.",
      body: "Iniciado por Foster (1998) e refinado na razão carga aguda/crônica (Gabbett 2016), o sRPE é usado por clubes da Premier League, federações olímpicas e todo ambiente de treino de elite que conhecemos. A gente só deixou ele com um toque a partir da mesa da cozinha.",
      link: "Leia a pesquisa por trás",
    },
    comparison: {
      heading: "Por que não usar um wearable ou uma planilha?",
      subhead: "Os wearables perdem o que o jogador sente. As planilhas morrem no porta-malas do carro.",
      columnFeature: "Capacidade",
      columnUs: "Vote.GameOn",
      columnThem: "Wearables / planilhas",
      rows: [
        "Funciona sem app para o jogador",
        "Captura esforço percebido, não só frequência cardíaca",
        "Grátis para os treinadores",
        "Vista de risco de lesão integrada (ACWR)",
        "Defina um PSE alvo por sessão",
        "Pensado para o futebol de base, não para academias profissionais",
      ],
      footnote: "A comparação reflete o posicionamento geral dos wearables de consumo e do acompanhamento manual em 2025.",
    },
    finalCta: {
      heading: "Comece a medir a carga ",
      headlineHighlight: "esta semana.",
      subhead: "Grátis para treinadores. Sem cartão. Os primeiros envios de sessão por nossa conta.",
      cta: "Crie seu time — grátis",
      footnote: "Um toque para o jogador. Um número para o treinador.",
    },
  },
  fr: {
    seo: {
      title: "Suivi de charge (RPE) pour les entraîneurs amateurs — Vote.GameOn",
      description:
        "Suivi de charge RPE de niveau pro pour le football amateur. Réponse du joueur en un clic, risque de blessure via l'ACWR, sans app pour les joueurs.",
    },
    srHeading: "Un suivi de charge qui marche — un clic par joueur",
    hero: {
      eyebrow: "Suivi de charge, en simple",
      headline: "Sachez à quel point vos joueurs travaillent dur.",
      headlineHighlight: "Avant qu'ils ne cassent.",
      subhead:
        "Un clic sur un chiffre de 1 à 10 après chaque séance. La méthode utilisée par les clubs d'élite — adaptée aux entraîneurs amateurs et aux parents qui les font tourner.",
      cta: "Créer une équipe gratuite",
      seeHow: "Voir comment ça marche",
      subLinks: {
        vote: "Voir le vote du joueur →",
        rsvp: "Voir comment marche la présence →",
        rpe: "Voir le suivi de charge (RPE) →",
        feedback: "Voir l'évaluation de l'entraîneur →",
      },
      mockSession: "Entraînement du mardi",
      mockQuestion: "C'était dur comment ?",
      mockFooter: "Un clic. Sans login. L'entraîneur voit instantanément.",
      mockBadge: "Charge saine · ACWR 0,9",
    },
    problem: {
      eyebrow: "Le problème caché de l'entraînement amateur",
      cards: [
        { title: "On ne voit pas la fatigue", body: "Quand un joueur sort en boitant, les signaux étaient là il y a deux semaines." },
        { title: "Chaque joueur est différent", body: "Mêmes exercices, charges très différentes. Sans chiffres, ce n'est que de l'intuition." },
        { title: "Les pics cassent les joueurs", body: "Les sauts brutaux de charge sont le meilleur prédicteur des lésions musculaires." },
      ],
    },
    howItWorks: {
      heading: "Trois étapes. Un clic par joueur.",
      subhead: "Du coup de sifflet final à une image complète de la charge — en moins de cinq minutes.",
      steps: [
        { badge: "5 secondes", title: "L'entraîneur envoie après l'entraînement", body: "Un clic envoie un SMS ou un e-mail à tous les joueurs. Rien à installer, aucun login à courser." },
        { badge: "1 clic", title: "Le joueur clique un chiffre", body: "De 1 à 10, avec des libellés en langage clair. La plupart répondent en quelques minutes." },
        { badge: "En direct", title: "L'entraîneur voit la charge", body: "Tendance par joueur, feu tricolore ACWR et une grille équipe-par-séance, en temps réel." },
      ],
    },
    scale: {
      heading: "Toute l'échelle sur un seul écran",
      subhead:
        "Une échelle de 1 à 10 avec des libellés en clair. La même que celle des scientifiques du sport — traduite pour les enfants, les parents et les entraîneurs du week-end.",
      footnote:
        "Les joueurs voient les mêmes couleurs et libellés dans l'app — un \"7\" veut dire la même chose le mardi qu'en mai.",
      items: [
        { label: "Repos", description: "Presque aucun effort" },
        { label: "Très léger", description: "Facile, on parle normalement" },
        { label: "Léger", description: "Rythme de récupération, confortable" },
        { label: "Modéré", description: "Respiration plus forte, on peut converser" },
        { label: "Moyen", description: "Effort normal d'entraînement" },
        { label: "Dur", description: "Essoufflé, à peine une phrase" },
        { label: "Très dur", description: "À la limite de l'inconfort" },
        { label: "Extrêmement dur", description: "Très difficile à tenir" },
        { label: "Presque max", description: "Presque impossible de continuer" },
        { label: "Maximum", description: "Tout ou rien, intenable" },
      ],
    },
    features: {
      heading: "Pour l'entraîneur qui est aussi parent, manager et lave les maillots",
      subhead: "Suivi de charge pro sans la lourdeur pro.",
      cards: [
        { title: "Pas de comptes pour les joueurs", body: "Les joueurs répondent depuis un lien unique par SMS ou e-mail. Rien à installer, rien à retenir." },
        { title: "Envoi SMS et e-mail", body: "Vous touchez chaque foyer là où il est déjà. Au choix par séance, ou par défaut au moyen enregistré." },
        { title: "Feu tricolore (ACWR)", body: "Vue en feu tricolore du ratio charge aiguë/chronique — le chiffre que les clubs d'élite utilisent pour détecter le risque de blessure avant qu'il ne survienne.", hrefLabel: "Qu'est-ce que l'ACWR ?" },
        { title: "Grille équipe-par-séance", body: "Une seule vue de tous les joueurs sur toutes les séances. Repérez le joueur silencieusement cuit." },
        { title: "RPE cible dans le plan", body: "Définissez un objectif avant l'entraînement ; signalez toute réponse qui s'écarte de plus de deux points." },
        { title: "Payez à l'envoi seulement", body: "Les entraîneurs utilisent l'app gratuitement. Rechargez de petits packs de crédits SMS/e-mail au besoin — sans abonnement." },
      ],
    },
    acwr: {
      eyebrow: "Nouveau pour vous ?",
      heading: "Attendez — c'est quoi l'ACWR (ratio charge aiguë/chronique) ?",
      subhead: "Le chiffre qui prédit en silence les lésions musculaires — expliqué en langage clair.",
      pieces: [
        { title: "Charge aiguë", sub: "7 derniers jours", body: "Combien le joueur a bossé cette semaine — chaque séance, additionnée." },
        { title: "Charge chronique", sub: "Moyenne mobile sur 28 jours", body: "Ce à quoi son corps est habitué le mois passé — la base à laquelle il s'est adapté." },
        { title: "Le ratio", sub: "Aiguë ÷ Chronique", body: "Quand la semaine en cours dépasse nettement la moyenne récente, le corps n'a pas encore adapté — et c'est là que les blessures montent." },
      ],
      bandTitle: "Les quatre zones de charge",
      bandSubtitle: "Les mêmes couleurs que dans le tableau de bord de l'entraîneur",
      zones: [
        { label: "Sous-charge", range: "< 0,8" },
        { label: "Zone idéale", range: "0,8 – 1,3" },
        { label: "Vigilance", range: "1,3 – 1,5" },
        { label: "Pic à risque", range: "> 1,5" },
      ],
      targetBadge: "Cible",
      closingBody:
        "Les clubs pros suivent ça depuis des années avec des gilets GPS et des scientifiques du sport. Nous le calculons à partir d'un seul clic par joueur après l'entraînement.",
      closingLinkLabel: "Voir la recherche",
    },
    science: {
      eyebrow: "Adossé à la science du sport",
      heading: "Le sRPE est la méthode de suivi de charge la plus validée au monde.",
      body: "Initié par Foster (1998) et raffiné en ratio charge aiguë/chronique (Gabbett 2016), le sRPE est utilisé par les clubs de Premier League, les fédérations olympiques et tous les environnements d'entraînement d'élite que nous connaissons. Nous l'avons juste rendu accessible depuis la table de la cuisine.",
      link: "Lire la recherche derrière",
    },
    comparison: {
      heading: "Pourquoi pas juste un wearable ou un tableur ?",
      subhead: "Les wearables ratent ce que le joueur ressent. Les tableurs meurent dans le coffre.",
      columnFeature: "Capacité",
      columnUs: "Vote.GameOn",
      columnThem: "Wearables / tableurs",
      rows: [
        "Marche sans app joueur",
        "Capture l'effort perçu, pas seulement la fréquence cardiaque",
        "Gratuit pour les entraîneurs",
        "Vue de risque de blessure intégrée (ACWR)",
        "Définir un RPE cible par séance",
        "Pensé pour l'amateur, pas les académies pro",
      ],
      footnote: "La comparaison reflète le positionnement général des wearables grand public et du suivi maison en 2025.",
    },
    finalCta: {
      heading: "Commencez à mesurer la charge ",
      headlineHighlight: "cette semaine.",
      subhead: "Gratuit pour les entraîneurs. Sans carte. Les premiers envois de séance sont offerts.",
      cta: "Créez votre équipe — gratuit",
      footnote: "Un clic pour le joueur. Un chiffre pour l'entraîneur.",
    },
  },
  ja: {
    seo: {
      title: "草の根コーチのための運動負荷トラッキング — Vote.GameOn",
      description:
        "草の根フットボールのためのプロ仕様のRPE負荷モニタリング。選手はワンタップで回答、ACWRによる怪我リスク管理、選手用アプリ不要。",
    },
    srHeading: "本当に動く負荷トラッキング — 選手ひとりにつきワンタップ",
    hero: {
      eyebrow: "運動負荷トラッキングを、シンプルに",
      headline: "選手がどれだけがんばっているかを知ろう。",
      headlineHighlight: "壊れてしまう前に。",
      subhead:
        "練習後に1から10までの数字をワンタップ。エリートクラブと同じ手法を、草の根コーチとその運営を支える保護者向けに調整しました。",
      cta: "無料でチームを作る",
      seeHow: "仕組みを見る",
      subLinks: {
        vote: "選手間投票を見る →",
        rsvp: "出欠確認の仕組みを見る →",
        rpe: "運動負荷トラッキングを見る →",
        feedback: "コーチ評価の仕組みを見る →",
      },
      mockSession: "火曜の練習",
      mockQuestion: "どれくらいキツかった？",
      mockFooter: "ワンタップ。ログイン不要。コーチにすぐ届きます。",
      mockBadge: "負荷は健全・ACWR 0.9",
    },
    problem: {
      eyebrow: "草の根トレーニングに潜む問題",
      cards: [
        { title: "疲労は見えない", body: "選手が足を引きずって出てくる頃には、警告サインは2週間前に出ていた。" },
        { title: "選手ごとに違う", body: "同じメニューでも負荷は全く違います。数字がなければ全部勘任せです。" },
        { title: "急な負荷増加が壊す", body: "負荷の急上昇は、軟部組織損傷の最も強い予測因子です。" },
      ],
    },
    howItWorks: {
      heading: "3ステップ。選手ひとりにつきワンタップ。",
      subhead: "終了ホイッスルから練習負荷の全体像まで — 5分以内。",
      steps: [
        { badge: "5秒", title: "コーチが練習後に送信", body: "ワンタップで全選手にSMSまたはメール送信。インストールも、ログイン追跡も不要。" },
        { badge: "ワンタップ", title: "選手が数字をタップ", body: "1〜10、分かりやすい日本語ラベル付き。多くは数分以内に返信します。" },
        { badge: "リアルタイム", title: "コーチが負荷を確認", body: "選手ごとの推移、ACWRの怪我リスク帯、チーム×セッションの一覧をリアルタイム表示。" },
      ],
    },
    scale: {
      heading: "スケール全体を1画面で",
      subhead:
        "1〜10のスケールに分かりやすいラベル。スポーツ科学者が使うのと同じスケールを、子ども・保護者・週末コーチ向けに翻訳しました。",
      footnote:
        "選手もアプリで同じ色とラベルを見ます — 火曜の「7」と5月の「7」は同じ意味です。",
      items: [
        { label: "休息", description: "ほぼ運動なし" },
        { label: "非常に軽い", description: "楽々。普通に会話できる" },
        { label: "軽い", description: "回復ペース、快適" },
        { label: "中程度", description: "呼吸が荒め、会話は可能" },
        { label: "普通", description: "通常の練習強度" },
        { label: "きつい", description: "息が上がる、一文なら言える" },
        { label: "とてもきつい", description: "不快になる寸前" },
        { label: "極めてきつい", description: "維持するのが非常に困難" },
        { label: "ほぼ最大", description: "続けるのがほぼ不可能" },
        { label: "最大", description: "全力、維持不可能" },
      ],
    },
    features: {
      heading: "保護者・監督・洗濯係も兼ねているコーチのために",
      subhead: "プロ級の負荷モニタリングを、プロ級の負担なしで。",
      cards: [
        { title: "選手アカウント不要", body: "選手はSMSやメールに届く使い切りリンクから回答。インストールも記憶も不要。" },
        { title: "SMS・メール配信", body: "保護者が普段いる場所に届きます。セッションごとに選択、または既定の連絡手段に自動送信。" },
        { title: "怪我リスク帯（ACWR）", body: "急性:慢性ワークロード比を信号色で表示 — エリートクラブが怪我リスクの兆候を捉えるのに使う指標です。", hrefLabel: "ACWRとは？" },
        { title: "チーム×セッション一覧", body: "全選手×全セッションを1画面で。密かに燃え尽きかけている選手を見逃しません。" },
        { title: "目標RPEをプランに設定", body: "練習前に目標を設定し、実測値が2ポイント以上ズレたら警告表示。" },
        { title: "配信分だけの課金", body: "コーチはアプリを無料で利用。SMS/メールの小額クレジットパックを必要に応じて追加 — サブスクなし。" },
      ],
    },
    acwr: {
      eyebrow: "この用語ははじめて？",
      heading: "そもそもACWR（急性:慢性ワークロード比）って？",
      subhead: "軟部組織損傷を静かに予測してくれる一つの数字を、分かりやすく解説。",
      pieces: [
        { title: "急性ワークロード", sub: "直近7日間", body: "この1週間で選手がどれだけ頑張ったか — 全セッションの合計。" },
        { title: "慢性ワークロード", sub: "28日間の移動平均", body: "この1か月で身体が慣れている水準 — 適応済みのベースライン。" },
        { title: "比率", sub: "急性 ÷ 慢性", body: "今週が最近の平均を大きく上回ると身体はまだ適応しておらず、怪我が急増するゾーンに入ります。" },
      ],
      bandTitle: "トレーニング負荷の4つのゾーン",
      bandSubtitle: "コーチ用ダッシュボードと同じ色分け",
      zones: [
        { label: "負荷不足", range: "< 0.8" },
        { label: "スイートスポット", range: "0.8 – 1.3" },
        { label: "注意", range: "1.3 – 1.5" },
        { label: "急増リスク", range: "> 1.5" },
      ],
      targetBadge: "目標",
      closingBody:
        "プロクラブはGPSベストとスポーツ科学者を使って何年も追跡してきました。私たちはこれを、練習後の選手ごとのワンタップから算出します。",
      closingLinkLabel: "研究を見る",
    },
    science: {
      eyebrow: "スポーツ科学の裏付けあり",
      heading: "Session-RPEは、世界で最も検証された負荷モニタリング手法です。",
      body: "Foster (1998) が確立し、急性:慢性ワークロード比（Gabbett 2016）として洗練されたsession-RPEは、プレミアリーグのクラブ、オリンピック連盟、私たちが知るあらゆるエリートトレーニング環境で使われています。私たちはそれを台所のテーブルからタップできるようにしただけです。",
      link: "背後にある研究を読む",
    },
    comparison: {
      heading: "ウェアラブルや表計算で十分では？",
      subhead: "ウェアラブルは選手の主観を捉えられない。表計算は車のトランクで死にます。",
      columnFeature: "機能",
      columnUs: "Vote.GameOn",
      columnThem: "ウェアラブル／表計算",
      rows: [
        "選手用アプリなしで動く",
        "心拍数だけでなく主観的努力度を計測",
        "コーチは無料",
        "怪我リスク表示（ACWR）を標準搭載",
        "セッションごとに目標RPEを設定",
        "プロアカデミーではなく草の根向けに設計",
      ],
      footnote: "民生向けウェアラブルや手作りトラッキングの2025年時点の一般的な位置づけを比較。",
    },
    finalCta: {
      heading: "運動負荷の記録を ",
      headlineHighlight: "今週から始めよう。",
      subhead: "コーチは無料。カード不要。最初のセッションの配信分は当方持ちです。",
      cta: "無料でチームを作る",
      footnote: "選手にはワンタップ。コーチには一つの数字。",
    },
  },
};
